<template>
  <div v-show="hasMore || loading" class="auto-load" ref="sentinel">
    <div class="auto-load-spinner" aria-hidden="true">
      <span></span><span></span><span></span>
    </div>
    <span class="auto-load-text">Indlæser flere opskrifter…</span>
  </div>
</template>

<script>
export default {
  name: 'LoadMore',
  data() {
    return {
      observer: null,
      loading: false
    }
  },
  computed: {
    nrToShow() {
      return this.$store.getters.nrOfRecipesToShow
    },
    filteredRecipes() {
      return this.$store.getters.filteredRecipes
    },
    hasMore() {
      return this.filteredRecipes.length > this.nrToShow
    },
    pageSize() {
      // Match the previous batch sizes from the old click-button.
      return this.$route.name === 'Search' ? 20 : 21
    }
  },
  methods: {
    loadMore() {
      if (!this.hasMore || this.loading) return
      this.loading = true
      // A tiny delay gives the spinner a moment to render so it doesn't flash
      // - more "felt" performance than functional pause.
      this.$nextTick(() => {
        this.$store.commit('setNrOfRecipesToShow', this.nrToShow + this.pageSize)
        // Release after a frame so the new tiles paint before we re-arm.
        requestAnimationFrame(() => { this.loading = false })
      })
    }
  },
  mounted() {
    if (typeof IntersectionObserver === 'undefined') {
      // Old browsers - fall back to a scroll listener.
      this.fallback = () => {
        const rect = this.$refs.sentinel && this.$refs.sentinel.getBoundingClientRect()
        if (rect && rect.top < window.innerHeight + 200) this.loadMore()
      }
      window.addEventListener('scroll', this.fallback, { passive: true })
      return
    }
    this.observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.loadMore()
      }
    }, {
      // Trigger ~one viewport before the user actually reaches the sentinel
      // so the next batch is already there when they get there.
      rootMargin: '0px 0px 600px 0px',
      threshold: 0
    })
    this.observer.observe(this.$refs.sentinel)
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
    if (this.fallback) window.removeEventListener('scroll', this.fallback)
  }
}
</script>

<style lang="scss" scoped>
.auto-load {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 30px 20px 40px;
  min-height: 80px;
}

.auto-load-text {
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: var(--dark);
  opacity: 0.7;
}

.auto-load-spinner {
  display: inline-flex;
  gap: 6px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--main);
    animation: auto-load-bounce 1.2s infinite ease-in-out both;
  }
  span:nth-child(1) { animation-delay: -0.32s; }
  span:nth-child(2) { animation-delay: -0.16s; }
}

@keyframes auto-load-bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
  40%           { transform: scale(1);   opacity: 1;   }
}
</style>
